import { AnalyzeType } from "./AnalyzeInterface";
import {
    CardColumn,
    FullHeightSegment,
    CenteredDiv,
    PurpleText,
    PurpleBullet,
    InlineTable,
    InlineTableCell,
    ButtonContainer,
    PurpleButton,
    TopContentDiv,
    StyledUl,
    CentreContentDiv,
    BottomContentDiv,
    Wrapper,
    Inner,
    PurpleButtonText,
    FixedTopChild,
    PurpleHeading,
    ScrollableList,
} from "./AnalyzeStyle";
import AnalyzeSaasLogo from "../../images/analyze/analyze-saas-logo.svg";
import AnalyzeOnPremLogo from "../../images/analyze/analyze-on-prem-logo.svg";
import { ApplicationContext, TranslationKey } from "../../components/ApplicationProvider";
import { useContext, useState, useCallback } from "react";
import { UserContext } from "../../components/User/UserProvider";
import AnalyzeSaaSModal from "./AnalyzeSaaSModal";
import AnalyzeSaasModalPopup from "./AnalyzeSaasModalPopup";
import { useMutation } from "@apollo/client";
import { CREATE_EVENT } from "../../graphql/Mutations/Event";
import { useCompanyAsAdminQuery } from "../../graphql";
import {
    EventCategory,
    METRIC_EVENT_CATALOG,
    MetricEventKey,
} from "@grabcad/company-server-shared/dist/types";

export interface ComparisonCardProps {
    element: string;
    logoHeader: string;
    headerDetails: string;
    whyChooseText: string;
    detailsArray: arrayDetails[];
    pointsToNote: arrayDetails[];
    bestForSteps: TranslationKey[];
    disabled?: boolean;
    loadingData?: boolean;
    navigateToAnalyzePages: (type: AnalyzeType) => void;
}

interface arrayDetails {
    heading: TranslationKey;
    description: TranslationKey;
}

export const ComparisonCard = ({
    element,
    logoHeader,
    headerDetails,
    whyChooseText,
    detailsArray,
    pointsToNote,
    bestForSteps,
    disabled,
    loadingData,
    navigateToAnalyzePages,
}: ComparisonCardProps) => {
    const { t } = useContext(ApplicationContext);
    const { isCompanyAdmin } = useContext(UserContext);
    const [showInfoModal, setShowInfoModal] = useState(false);
    const [triggerCreateEvent] = useMutation(CREATE_EVENT);
    const { company } = useCompanyAsAdminQuery();

    const [showModal, setShowModal] = useState(false);
    const analyzeType =
        element === "ANALYZE_SAAS" ? AnalyzeType.AnalyzeSaaS : AnalyzeType.AnalyzeOnPrem;

    const handleButtonClick = useCallback(
        (type: AnalyzeType) => {
            if (company?.id && !company.internal) {
                // Map AnalyzeType → MetricEventKey inside the function
                const ANALYZE_EVENT_MAP: Record<AnalyzeType, MetricEventKey> = {
                    [AnalyzeType.AnalyzeSaaS]: MetricEventKey.ANALYZE_SAAS_MODAL_CONFIRMED,
                    [AnalyzeType.AnalyzeOnPrem]: MetricEventKey.ANALYZE_ON_PREM_CLICKED,
                };

                const metricKey = ANALYZE_EVENT_MAP[type];

                if (metricKey) {
                    const { action, description } = METRIC_EVENT_CATALOG[metricKey];

                    // Fire-and-forget event logging
                    triggerCreateEvent({
                        variables: {
                            events: [
                                {
                                    event: EventCategory.GC_ANALYZE,
                                    action,
                                    description,
                                },
                            ],
                            companyId: company.id,
                        },
                    }).catch((err) => console.error("Failed to log Analyze event:", err));
                }
            }
        },
        [company, triggerCreateEvent]
    );

    const handleAnalyzeConfirm = useCallback(() => {
        setShowInfoModal(false);
        handleButtonClick(analyzeType);
        navigateToAnalyzePages(analyzeType);
    }, [analyzeType, handleButtonClick, navigateToAnalyzePages]);

    return (
        <CardColumn>
            <FullHeightSegment>
                <CenteredDiv>
                    <img
                        src={element === "ANALYZE_SAAS" ? AnalyzeSaasLogo : AnalyzeOnPremLogo}
                        alt={
                            element === "ANALYZE_SAAS" ? "Analyze Saas Logo" : "Analyze OnPrem Logo"
                        }
                    />
                    <PurpleHeading>{logoHeader}</PurpleHeading>
                    <span>{headerDetails}</span>
                </CenteredDiv>
                <TopContentDiv className="content active">
                    <PurpleText>{whyChooseText}</PurpleText>
                    <ScrollableList>
                        <StyledUl>
                            {detailsArray.map((step, idx) => (
                                <PurpleBullet key={idx}>
                                    <strong>{t(step.heading)}</strong> {t(step.description)}
                                </PurpleBullet>
                            ))}
                        </StyledUl>
                    </ScrollableList>
                </TopContentDiv>
                <CentreContentDiv className="content active">
                    <PurpleText>{t("analyze_selection.considerations")}</PurpleText>
                    <ScrollableList>
                        {pointsToNote.map((step, idx) => (
                            <InlineTable key={idx}>
                                <tbody>
                                    <tr>
                                        <InlineTableCell>
                                            <i
                                                aria-hidden="true"
                                                style={{ color: "#D6956B" }}
                                                className="warning sign icon"
                                            />
                                        </InlineTableCell>
                                        <td style={{ verticalAlign: "middle" }}>
                                            <strong>{t(step.heading)}</strong> {t(step.description)}
                                        </td>
                                    </tr>
                                </tbody>
                            </InlineTable>
                        ))}
                    </ScrollableList>
                </CentreContentDiv>
                <BottomContentDiv className="content active">
                    <PurpleText>{t("analyze_selection.best_for")}</PurpleText>
                    <ScrollableList>
                        {bestForSteps.map((key, idx) => (
                            <InlineTable key={idx}>
                                <tr>
                                    <InlineTableCell>
                                        <i
                                            aria-hidden="true"
                                            style={{ color: "#700AC1" }}
                                            className="check circle outline icon"
                                        />
                                    </InlineTableCell>
                                    <td style={{ verticalAlign: "middle" }}>{t(key)}</td>
                                </tr>
                            </InlineTable>
                        ))}
                    </ScrollableList>
                </BottomContentDiv>
                {/* <div style={{ marginTop: "15px" }}></div> */}
                <ButtonContainer>
                    <FixedTopChild>
                        {loadingData ? (
                            <PurpleButton disabled={loadingData}>Loading...</PurpleButton>
                        ) : (
                            <>
                                <PurpleButton
                                    onClick={() => {
                                        if (element === "ANALYZE_SAAS") {
                                            // SaaS: open modal, event will be fired on modal confirmation
                                            setShowInfoModal(true);
                                        } else {
                                            // On-Prem: log event and navigate immediately
                                            handleButtonClick(analyzeType);
                                            navigateToAnalyzePages(analyzeType);
                                        }
                                    }}
                                    disabled={disabled || !isCompanyAdmin}
                                >
                                    {element === "ANALYZE_SAAS"
                                        ? t("analyze_selection.saas_button")
                                        : t("analyze_selection.on_prem_button")}
                                </PurpleButton>

                                {element === "ANALYZE_SAAS" && (
                                    <AnalyzeSaasModalPopup
                                        open={showInfoModal}
                                        onClose={() => setShowInfoModal(false)}
                                        onConfirm={handleAnalyzeConfirm}
                                    />
                                )}
                            </>
                        )}
                    </FixedTopChild>

                    {/* Show disabled checklist if SaaS is disabled */}
                    {element === "ANALYZE_SAAS" && disabled && (
                        <>
                            <Wrapper>
                                <Inner>
                                    <PurpleButtonText onClick={() => setShowModal(true)}>
                                        {t("analyze_selection.analyze_checklist")}
                                    </PurpleButtonText>
                                </Inner>
                            </Wrapper>
                            {showModal && <AnalyzeSaaSModal onClose={() => setShowModal(false)} />}
                        </>
                    )}
                </ButtonContainer>
            </FullHeightSegment>
        </CardColumn>
    );
};
